import React from 'react'
import type { IntlShape } from 'react-intl'

import type { PlanKey } from '@slite/pricing'
import {
  getSlitePlans,
  getSlitePricingFeatures,
  SLITE_PLAN_PRICES,
} from '@slite/pricing'

import type { SourceEntry } from '../../components/SourceIconsRow'
import { createRoutes } from '../../routes'

export type PricingPlan = {
  id: string
  title: string
  description: string

  accentuated?: boolean
  btnVariant: 'primary' | 'secondary'
  price: {
    monthly: number
    yearly: number
  } | null
  features: {
    label: string
    indent?: boolean
    showSourceIcons?: boolean
    comingSoon?: boolean
    tooltip?: React.ReactNode
    red?: boolean
  }[]
  action: {
    label: string
    href: string
  }
}

export type PricingFeatures = {
  id: string
  title: string
  items: {
    title: string
    description?: string
    comingSoon?: boolean
    mutedStandard?: boolean
    /** Red emphasis on feature title (e.g. compliance callouts) */
    titleRed?: boolean
    /** Red emphasis on Basic column cell when value is a string */
    standardRed?: boolean
    /** Muted subtext shown beneath the Basic column value */
    standardSubtext?: string
    /** Hover tooltip shown on the Basic-column subtext */
    standardSubtextTooltip?: string
    sourceIcons?: SourceEntry[]
    standard: boolean | string
    premium: boolean | string
    enterprise: boolean | string
  }[]
}[]

function renderHighlightTooltip(tooltip: {
  label: string
  linkLabel?: string
  href?: string
}) {
  if (!tooltip.href || !tooltip.linkLabel) {
    return tooltip.label
  }
  return (
    <>
      {tooltip.label}{' '}
      <a
        href={tooltip.href}
        target="_blank"
        rel="noreferrer"
        className="shade-00"
        style={{ textDecoration: 'none' }}
      >
        <span
          className="flexbox-inline flexbox-align-center flexbox-column-gap-4"
          style={{ borderBottom: '1px solid currentColor' }}
        >
          {tooltip.linkLabel}
          <span aria-hidden className="flexbox-no-shrink">
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
              <path
                d="M1.5 8.5L8.5 1.5M8.5 1.5H5M8.5 1.5V4.5"
                stroke="currentColor"
                strokeWidth="1"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
        </span>
      </a>
    </>
  )
}

export function getPricingPlans(intl: IntlShape): PricingPlan[] {
  const routes = createRoutes(intl.locale)
  const actionLabels: Record<PlanKey, { id: string; defaultMessage: string }> =
    {
      basic: { id: 'pricing.basic.action', defaultMessage: 'Get started' },
      pro: { id: 'pricing.core.action', defaultMessage: 'Get started' },
      enterprise: {
        id: 'pricing.enterprise.action',
        defaultMessage: 'Contact Sales',
      },
    }
  return getSlitePlans(intl).map((plan) => ({
    id: plan.id,
    title: plan.title,
    description: plan.description,
    accentuated: plan.id === 'pro',
    btnVariant: plan.id === 'pro' ? 'primary' : 'secondary',
    price: plan.id === 'enterprise' ? null : SLITE_PLAN_PRICES[plan.id],
    features: plan.highlights.map((highlight) => ({
      label: highlight.label,
      indent: highlight.indent,
      showSourceIcons: highlight.showSourceIcons,
      tooltip: highlight.tooltip
        ? renderHighlightTooltip(highlight.tooltip)
        : undefined,
    })),
    action: {
      label: intl.formatMessage(actionLabels[plan.id]),
      href: plan.id === 'enterprise' ? routes.bookDemo() : routes.auth.signup(),
    },
  }))
}

export function getPricingFeatures(intl: IntlShape): PricingFeatures {
  return getSlitePricingFeatures(intl).map((group) => ({
    id: group.id,
    title: group.title,
    items: group.items.map((item) => ({
      title: item.title,
      description: item.description,
      sourceIcons: item.sourceIcons?.map((iconUrl) => ({ iconUrl })),
      mutedStandard: item.mutedBasic,
      standardSubtext: item.basicSubtext,
      standardSubtextTooltip: item.basicSubtextTooltip,
      standard: item.basic,
      premium: item.pro,
      enterprise: item.enterprise,
    })),
  }))
}

export function getPricingFAQ(intl: IntlShape) {
  const routes = createRoutes(intl.locale)
  return [
    {
      question: intl.formatMessage({
        id: 'pricing.faq.question1',
        defaultMessage: 'How does the 14 days free trial work?',
      }),
      answer: intl.formatMessage({
        id: 'pricing.faq.answer1',
        defaultMessage:
          "Once you sign up for Slite, your 14-day free trial begins. During this period you can explore the product and everything included in the Basic and Pro plans — no credit card required. You'll have the option to book a demo with one of our Slite experts at any point during the trial.",
      }),
    },
    {
      question: intl.formatMessage({
        id: 'pricing.faq.question2',
        defaultMessage: 'What happens after the Free Trial has ended?',
      }),
      answer: intl.formatMessage({
        id: 'pricing.faq.answer2',
        defaultMessage:
          "Once the 14 day free trial has ended, you will see a message that purchasing one of our subscription plans is necessary to continue using Slite. You'll need to be a workspace admin to purchase a plan.",
      }),
    },
    {
      question: intl.formatMessage({
        id: 'pricing.faq.question3',
        defaultMessage:
          'Do you offer discounts for non-profit and academic organizations?',
      }),
      answer: intl.formatMessage({
        id: 'pricing.faq.answer3',
        defaultMessage:
          'Yes, we offer discounts to eligible non-profit and academic organizations on the Basic plan. Contact support@slite.com to find out more and redeem this offer.',
      }),
    },
    {
      question: intl.formatMessage({
        id: 'pricing.faq.question4',
        defaultMessage:
          'Can I add extra members to the workspace and how are these billed?',
      }),
      answer: intl.formatMessage({
        id: 'pricing.faq.answer4',
        defaultMessage:
          "You can add extra users to your workspace directly in the product. If you are on our monthly plan, these extra users will be billed on your next invoice. If you are on our yearly plan, we will prorate the costs. We'll review the number of members each month on the day you have started your subscription. If the number of users is higher than your current baseline, we will prorate the extra licenses and bill you for the remaining months on your contract.",
      }),
    },
    {
      question: intl.formatMessage({
        id: 'pricing.faq.question5',
        defaultMessage: 'What happens if I remove members from the account?',
      }),
      answer: intl.formatMessage({
        id: 'pricing.faq.answer5',
        defaultMessage:
          'At any time you can remove users from your account. When you remove a user, this frees up a license that can be used by someone else. If you do not assign the license to someone else, the lower number of licenses will be reflected on your renewal invoice.',
      }),
    },
    {
      question: intl.formatMessage({
        id: 'pricing.faq.question6',
        defaultMessage: 'Is Slite HIPAA compliant?',
      }),
      answer: intl.formatMessage(
        {
          id: 'pricing.faq.answer6',
          defaultMessage:
            'Yes, we’re HIPAA compliant and can sign a BAA for eligible plans. To learn more about how we protect healthcare data, get in touch with our {link}.',
        },
        {
          link: (
            <a href={routes.bookDemo()}>
              {intl.formatMessage({
                id: 'pricing.faq.answer6.linkText',
                defaultMessage: 'sales team',
              })}
            </a>
          ),
        }
      ),
    },
  ]
}
